import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

const KEY = 'tasks';

// All tasks — always enabled, used by the /tasks page
export function useAllTasks(params = {}) {
  return useQuery({
    queryKey: [KEY, 'all', params],
    queryFn: () => api.get('/tasks', { params }).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
  });
}

// Embedded tasks — enabled only when account_id or deal_id is present
export function useTasks(params = {}) {
  return useQuery({
    queryKey: [KEY, params],
    queryFn: () => api.get('/tasks', { params }).then((r) => r.data),
    enabled: !!(params.account_id || params.deal_id),
    staleTime: 2 * 60 * 1000,
  });
}

// Current user's pending tasks with overdue/today flags
export function useMyTasks() {
  return useQuery({
    queryKey: [KEY, 'my'],
    queryFn: () => api.get('/tasks/my').then((r) => r.data),
    refetchInterval: 5 * 60 * 1000, // refresh every 5 min
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/tasks', body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useUpdateTask(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch(`/tasks/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/tasks/${id}/complete`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}
